import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownwardOutlined';
import ClearIcon from '@mui/icons-material/ClearOutlined';
import { brand } from '../../theme';
import { getAnnotationCategory, withAlpha } from '../../utils/annotationCategories';
import { formatDuration, formatTimecode } from '../../utils/timecode';
import EmptyState from '../ui/EmptyState';
import { StudioAnnotation, isRangeAnnotation } from './annotationModel';

const TC_FONT = '"JetBrains Mono", "SFMono-Regular", Menlo, monospace';

/** A category is shown by its colour dot and its name in ink; the colour stays on the dot, the rule and the marker. */
const CategoryDot: React.FC<{ color: string; size?: number; className?: string }> = ({ color, size = 8, className }) => (
  <Box
    component="span"
    aria-hidden="true"
    className={className}
    sx={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0 }}
  />
);

const actionButtonSx = {
  color: 'text.secondary',
  '&:hover': { color: 'text.primary' },
  // Bigger targets for fingers.
  '@media (pointer: coarse)': { width: 40, height: 40 },
} as const;

interface AnnotationCardProps {
  annotation: StudioAnnotation;
  index: number;
  fps: number;
  active: boolean;
  selected: boolean;
  readOnly: boolean;
  onSeek: (annotation: StudioAnnotation) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  extra?: React.ReactNode;
  /** Shown instead of the category's own name (see `getCategoryLabel`). */
  categoryLabel?: string;
}

const AnnotationCard = memo(function AnnotationCard({
  annotation,
  index,
  fps,
  active,
  selected,
  readOnly,
  onSeek,
  onEdit,
  onDelete,
  extra,
  categoryLabel,
}: AnnotationCardProps) {
  const category = getAnnotationCategory(annotation.category, annotation.color);
  const range = isRangeAnnotation(annotation);
  const start = formatTimecode(annotation.time, fps);
  const hasActions = !readOnly && Boolean(onEdit || onDelete);

  return (
    <Box
      data-annotation-id={annotation.id}
      data-active={active ? 'true' : 'false'}
      data-selected={selected ? 'true' : 'false'}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        flexShrink: 0,
        borderRadius: 2,
        border: `1px solid ${selected ? brand.copper : brand.border}`,
        // The selected note gets a 2px copper edge without shifting the layout.
        boxShadow: selected ? `inset 0 0 0 1px ${brand.copper}` : 'none',
        bgcolor: active ? brand.ivory : 'background.paper',
        transition: 'background-color 160ms ease, border-color 160ms ease',
        overflow: 'hidden',
        '&:hover': { borderColor: selected ? brand.copper : brand.borderStrong },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: active ? 4 : 3,
          bgcolor: category.color,
        },
        // Row actions appear on hover, on keyboard focus inside the row and on
        // the selected row; touch screens have no hover, so they always show.
        '& .annotation-actions': { opacity: 0, transition: 'opacity 120ms ease' },
        '&:hover .annotation-actions, &:focus-within .annotation-actions, &[data-selected="true"] .annotation-actions': { opacity: 1 },
        '@media (hover: none), (pointer: coarse)': { '& .annotation-actions': { opacity: 1 } },
      }}
    >
      <ButtonBase
        onClick={() => onSeek(annotation)}
        aria-label={`Play from ${start}: ${annotation.text}`}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'block',
          textAlign: 'left',
          pl: 2,
          pr: 1,
          py: 1.25,
          '&:focus-visible': { outline: `2px solid ${brand.copper}`, outlineOffset: -2 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.25, flexWrap: 'wrap', mb: 0.5 }}>
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary', fontVariantNumeric: 'tabular-nums', minWidth: 18 }}>
            {index + 1}
          </Typography>
          <Typography component="span" sx={{ fontFamily: TC_FONT, fontVariantNumeric: 'tabular-nums', fontSize: 12.5, fontWeight: 700, color: 'text.primary' }}>
            {start}
            {range && (
              <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                {' → '}{formatTimecode(annotation.endTime as number, fps)}
              </Box>
            )}
          </Typography>
          {range && (
            <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {formatDuration((annotation.endTime as number) - annotation.time)}
            </Typography>
          )}
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12, fontWeight: 600, color: 'text.primary' }}>
            <CategoryDot color={category.color} />
            {categoryLabel || category.label}
          </Box>
          {active && (
            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: 'primary.dark' }}>
              Now
            </Box>
          )}
        </Box>
        <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
          {annotation.text}
        </Typography>
        {extra}
      </ButtonBase>

      {hasActions && (
        <Box className="annotation-actions" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, p: 0.5 }}>
          {onEdit && (
            <Tooltip title="Edit annotation">
              <IconButton size="small" aria-label="Edit annotation" onClick={() => onEdit(annotation.id)} sx={actionButtonSx}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Delete annotation">
              <IconButton
                size="small"
                aria-label="Delete annotation"
                onClick={() => onDelete(annotation.id)}
                sx={{ ...actionButtonSx, '&:hover': { color: 'error.main' } }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  );
});

export interface AnnotationPanelProps {
  annotations: StudioAnnotation[];
  fps: number;
  activeIds: string[];
  selectedId?: string | null;
  playing?: boolean;
  readOnly?: boolean;
  title?: string;
  onSeek: (annotation: StudioAnnotation) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  /** Buttons in the header (export, …). */
  headerActions?: React.ReactNode;
  /** Shown under each note (e.g. linked tasks in the editor workspace). */
  renderExtra?: (annotation: StudioAnnotation) => React.ReactNode;
  /**
   * A name to show instead of the note's category name, e.g. "Camera cut"
   * for a multicam cut stored as a scene change. Return nothing to keep the
   * category's own name. Filters and search use the name shown.
   */
  getCategoryLabel?: (annotation: StudioAnnotation) => string | null | undefined;
  emptyTitle?: string;
  emptyHint?: string;
  /** CSS height of the scrolling list. */
  listMaxHeight?: string | number;
  testId?: string;
}

/**
 * The notes list: count, search, category filters, time sort, and cards that
 * seek on click and light up while their moment plays.
 */
const AnnotationPanel: React.FC<AnnotationPanelProps> = ({
  annotations,
  fps,
  activeIds,
  selectedId = null,
  playing = false,
  readOnly = false,
  title = 'Notes',
  onSeek,
  onEdit,
  onDelete,
  headerActions,
  renderExtra,
  getCategoryLabel,
  emptyTitle = 'No annotations yet',
  emptyHint = 'Pause on a moment and type below the video, or press A, to add the first note.',
  listMaxHeight = 'calc(100vh - 260px)',
  testId = 'annotation-panel',
}) => {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const listRef = useRef<HTMLDivElement | null>(null);

  /** The category as shown for a note: its filter key, name and colour. */
  const describe = useCallback((annotation: StudioAnnotation) => {
    const category = getAnnotationCategory(annotation.category, annotation.color);
    const override = getCategoryLabel?.(annotation)?.trim();
    return override
      ? { key: `${category.id}:${override.toLowerCase()}`, label: override, color: category.color, override }
      : { key: category.id, label: category.label, color: category.color, override: undefined };
  }, [getCategoryLabel]);

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, { label: string; color: string; count: number }>();
    for (const annotation of annotations) {
      const shown = describe(annotation);
      const entry = counts.get(shown.key) ?? { label: shown.label, color: shown.color, count: 0 };
      entry.count += 1;
      counts.set(shown.key, entry);
    }
    return Array.from(counts.entries());
  }, [annotations, describe]);

  const numbered = useMemo(
    () => [...annotations].sort((a, b) => a.time - b.time).map((annotation, index) => ({ annotation, index })),
    [annotations]
  );

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = numbered.filter(({ annotation }) => {
      const shown = describe(annotation);
      if (categoryFilter.length > 0 && !categoryFilter.includes(shown.key)) return false;
      if (!needle) return true;
      return (
        annotation.text.toLowerCase().includes(needle) ||
        shown.label.toLowerCase().includes(needle) ||
        formatTimecode(annotation.time, fps).includes(needle)
      );
    });
    return order === 'asc' ? filtered : [...filtered].reverse();
  }, [numbered, search, categoryFilter, order, fps, describe]);

  const activeSet = useMemo(() => new Set(activeIds), [activeIds]);
  const firstActive = activeIds[0] ?? null;

  // Keep the playing note (or a note selected on the timeline) in view.
  useEffect(() => {
    const target = playing ? firstActive : selectedId;
    const list = listRef.current;
    if (!target || !list) return;
    const escaped = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(target) : target.replace(/"/g, '\\"');
    const card = list.querySelector<HTMLElement>(`[data-annotation-id="${escaped}"]`);
    if (!card) return;
    const top = card.offsetTop;
    const bottom = top + card.offsetHeight;
    if (top < list.scrollTop || bottom > list.scrollTop + list.clientHeight) {
      const next = Math.max(0, top - 8);
      if (typeof list.scrollTo === 'function') list.scrollTo({ top: next, behavior: 'smooth' });
      else list.scrollTop = next;
    }
  }, [firstActive, selectedId, playing]);

  const toggleCategory = (id: string) =>
    setCategoryFilter((current) => (current.includes(id) ? current.filter((c) => c !== id) : [...current, id]));

  const filtering = search.trim().length > 0 || categoryFilter.length > 0;

  return (
    <Paper
      variant="outlined"
      data-testid={testId}
      sx={{ borderRadius: 3, borderColor: brand.border, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
    >
      <Box sx={{ px: 2, pt: 1.75, pb: 1.25, borderBottom: `1px solid ${brand.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, fontSize: 16 }}>
            {title}
          </Typography>
          <Chip
            size="small"
            label={filtering ? `${visible.length} of ${annotations.length}` : `${annotations.length} ${annotations.length === 1 ? 'note' : 'notes'}`}
            sx={{ fontVariantNumeric: 'tabular-nums', height: 22 }}
          />
          <Box sx={{ flex: 1 }} />
          {headerActions}
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search notes"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            inputProps={{ 'aria-label': 'Search notes' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={order}
            onChange={(_, value) => value && setOrder(value)}
            aria-label="Sort by time"
          >
            <ToggleButton value="asc" aria-label="Earliest first">
              <Tooltip title="Earliest first"><ArrowUpwardIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="desc" aria-label="Latest first">
              <Tooltip title="Latest first"><ArrowDownwardIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {categoryCounts.length > 1 && (
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }} role="group" aria-label="Filter by category">
            {categoryCounts.map(([id, entry]) => {
              const selected = categoryFilter.includes(id);
              return (
                <Chip
                  key={id}
                  size="small"
                  variant="outlined"
                  clickable
                  onClick={() => toggleCategory(id)}
                  aria-pressed={selected}
                  label={`${entry.label} ${entry.count}`}
                  icon={<CategoryDot color={entry.color} />}
                  sx={{
                    height: 26,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    color: selected ? 'primary.dark' : 'text.primary',
                    borderColor: selected ? brand.copper : brand.borderStrong,
                    bgcolor: selected ? withAlpha(brand.copper, 0.08) : 'background.paper',
                    '& .MuiChip-icon': { ml: 1, mr: -0.25 },
                    '&&:hover': { bgcolor: selected ? withAlpha(brand.copper, 0.14) : brand.ivory },
                    '&.Mui-focusVisible': { outline: `2px solid ${brand.copper}`, outlineOffset: 1 },
                  }}
                />
              );
            })}
            {categoryFilter.length > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label="Clear filters"
                onClick={() => setCategoryFilter([])}
                sx={{ height: 26, color: 'text.secondary', borderColor: 'transparent', '&&:hover': { bgcolor: brand.ivory } }}
              />
            )}
          </Box>
        )}
      </Box>

      <Box
        ref={listRef}
        sx={{ position: 'relative', overflowY: 'auto', maxHeight: listMaxHeight, minHeight: 160, p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {annotations.length === 0 ? (
          <EmptyState compact icon={<ChatBubbleOutlineIcon />} title={emptyTitle} description={emptyHint} />
        ) : visible.length === 0 ? (
          <EmptyState
            compact
            icon={<SearchIcon />}
            title="No notes match these filters"
            description="Try another search or category."
            secondaryActionLabel="Clear filters"
            onSecondaryAction={() => {
              setSearch('');
              setCategoryFilter([]);
            }}
          />
        ) : (
          visible.map(({ annotation, index }) => (
            <AnnotationCard
              key={annotation.id}
              annotation={annotation}
              index={index}
              fps={fps}
              active={activeSet.has(annotation.id)}
              selected={selectedId === annotation.id}
              readOnly={readOnly}
              onSeek={onSeek}
              onEdit={onEdit}
              onDelete={onDelete}
              extra={renderExtra?.(annotation)}
              categoryLabel={getCategoryLabel ? describe(annotation).override : undefined}
            />
          ))
        )}
      </Box>
    </Paper>
  );
};

export default AnnotationPanel;
